import React, { useEffect, useRef } from 'react';
import { OrderSummary } from './OrderSummary';
import { usePaymentForm } from '../hooks/usePaymentForm';
import { BillingForm } from './BillingForm';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { BuyerForm } from './BuyerForm';
import { ShareForm } from './Share';
import Delivery from './DeliveryForm';
import { Loader } from './Loader';
import { Button } from './Button';
import { buildWidgetAnalyticsContext, postParentAnalyticsEvent } from '../analytics/widgetTracking';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY || '');

const PaymentComponent = () => {
	const {
		handleShippingAddressChange,
		setPrimaryBuyerDeliveryMethod,
		updatePayment,
		addCoBuyer,
		removePayment,
		saveAndCollapseForm,
		expandForm,
		collapsedForms,
		errors,
		setPayments,
		handleContinue,
		handleEditConfig,
		hideQuantity,
		remainingQuantity,
		orderAmount,
		handleTitle,
		disableContinue,
		loader,
		hasPaid,
		groupBuyResponse,
		groupBuySettings,
		config,
		responseError,
		payments,
		setStep,
		step,
		totalPaymentAmount,
	} = usePaymentForm();

	const appearance = {
		theme: 'stripe' as 'stripe',
	};

	const options = {
		clientSecret: groupBuyResponse?.clientSecret,
		appearance,
	};

	const buttonConfig = groupBuySettings?.brand_color
		? `bg-[${groupBuySettings?.brand_color}] text-white`
		: `bg-indigo-600 text-white hover:bg-indigo-700`;

	const trackedStepRef = useRef<number | null>(null);

	useEffect(() => {
		if (trackedStepRef.current === step) {
			return;
		}

		trackedStepRef.current = step;

		const stepEventMap: Record<number, string> = {
			1: 'cobuyr_widget_details_step_viewed',
			2: 'cobuyr_widget_delivery_step_viewed',
			3: 'cobuyr_widget_payment_step_viewed',
			4: 'cobuyr_widget_share_step_viewed',
		};

		const eventName = stepEventMap[step];

		if (!eventName) {
			return;
		}

		postParentAnalyticsEvent(eventName, {
			...buildWidgetAnalyticsContext(config),
			participant_count: payments.length,
			cobuyer_count: Math.max(payments.length - 1, 0),
			group_total: Number(totalPaymentAmount.toFixed(2)),
		});
	}, [config, payments.length, step, totalPaymentAmount]);

	return (
		<div className="flex justify-between sm:flex-row flex-col w-full min-h-[30rem] ">
			<div className="sm:mr-10 sm:order-first order-last md:w-6/12 w-full flex flex-col justify-between">
				{step === 1 && loader && (
					<div className="min-h-[200px] flex items-center justify-center">
						<Loader color={groupBuySettings?.brand_color} />
					</div>
				)}

				{step === 1 && !loader && groupBuySettings && (
					<BuyerForm
						payments={payments}
						updatePayment={updatePayment}
						addCoBuyer={addCoBuyer}
						removePayment={removePayment}
						saveAndCollapseForm={saveAndCollapseForm}
						expandForm={expandForm}
						collapsedForms={collapsedForms}
						errors={errors}
						settings={groupBuySettings}
						color={groupBuySettings?.brand_color}
						defaultPhoneCode={groupBuySettings.business.country}
						includeQuantity={!hideQuantity && config.order.length === 1 && remainingQuantity > 0}
						config={config}
						orderAmount={orderAmount}
						totalPaymentAmount={totalPaymentAmount}
					/>
				)}

				{step === 2 && (
					<>
						<Delivery
							onProceed={handleContinue}
							isLoading={loader}
							groupBuySettings={groupBuySettings}
							buttonConfig={buttonConfig}
							goBack={() => {
								setStep(1);
								// // remove the last saved cobuyr
								setPayments((prev) => prev.filter((_, idx) => idx !== prev.length - 1));
								// validateForm();
							}}
							onChange={(field, value) => {
								if (field === 'delivery_method') {
									return setPrimaryBuyerDeliveryMethod(value);
								}
								handleShippingAddressChange(field, value);
							}}
							config={config}
						/>
						{responseError && <p className="text-red-500 text-xs">{responseError}</p>}
					</>
				)}

				{!hasPaid && step === 1 && (
					<Button
						title={handleTitle()}
						className={buttonConfig}
						disabled={step > 1 ? false : disableContinue}
						isLoading={loader && step !== 1}
						onClick={() => {
							handleContinue();
						}}
					/>
				)}

				{step === 3 && groupBuyResponse?.clientSecret && (
					<Elements options={options} stripe={stripePromise}>
						<BillingForm
							setStep={setStep}
							currency={groupBuySettings?.country?.symbol || ''}
							amount={groupBuyResponse.transaction.subTotal}
							brandColor={groupBuySettings?.brand_color}
						/>
					</Elements>
				)}

				{step === 4 && (
					<ShareForm
						businessName={groupBuySettings?.business.business_name || ''}
						brandColor={groupBuySettings?.brand_color}
						responseId={groupBuyResponse?.transaction.groupbuy_transaction_id || ''}
						publicKey={config.publicKey}
						options={payments.slice(1).map((payment) => ({ value: payment.email || '', label: payment.name || '' }))}
						config={config}
					/>
				)}
			</div>
			<div className="md:w-6/12 w-full md:mt-2 mb-2 sm:order-last">
				<OrderSummary
					summary={payments}
					remainingQuantity={remainingQuantity}
					hasQuantity={!hideQuantity && config.order.length === 1}
					handleEditConfig={handleEditConfig}
					currency={groupBuySettings?.country?.symbol || ''}
					order={config.order}
					totalAmount={orderAmount}
					step={step}
					groupTotal={totalPaymentAmount}
					config={config}
				/>
			</div>
		</div>
	);
};

export default PaymentComponent;
